import React from 'react';
import { Avatar, Image } from 'antd';
import { CameraOutlined, DeleteOutlined } from '@ant-design/icons';

interface ImagePreviewAvatarProps {
  src?: string;
  size?: number;
  onRemove?: () => void;
  showRemoveButton?: boolean;
}

/**
 * Reusable Image Preview Avatar Component
 * 
 * Best Practices:
 * - Wraps Ant Design Image component with native preview capability
 * - Clicking on avatar opens full-size image in lightbox
 * - Shows delete button when image is present
 * - Fallback to camera icon when no image
 * 
 * @example
 * <ImagePreviewAvatar 
 *   src={avatarPreview} 
 *   size={80}
 *   onRemove={handleRemoveAvatar}
 *   showRemoveButton={true}
 * />
 */
const ImagePreviewAvatar: React.FC<ImagePreviewAvatarProps> = ({
  src,
  size = 80,
  onRemove,
  showRemoveButton = true,
}) => {
  // If no image, show placeholder avatar
  if (!src) {
    return (
      <Avatar 
        size={size} 
        icon={<CameraOutlined />}
        style={{ backgroundColor: '#f0f0f0', color: '#999' }}
      />
    );
  }

  // If image exists, show it with preview capability
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Image
        src={src}
        alt="Avatar"
        preview={{
          mask: 'Phóng to',
          maskClassName: 'image-preview-mask',
        }}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          cursor: 'pointer',
        }}
      />

      {showRemoveButton && onRemove && (
        <DeleteOutlined
          onClick={onRemove}
          style={{
            position: 'absolute',
            top: -5,
            right: -5,
            color: '#ff4d4f',
            background: 'white',
            borderRadius: '50%',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 12,
            lineHeight: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        />
      )}
    </div>
  );
};

export default ImagePreviewAvatar;
